(function(w) {
  'use strict';
  
  Cnvs.Images = {

    // ================================================================
    // UPLOAD MANAGER
    // ================================================================

    uploadManager: {
      isRunning: false,
      currentUpload: null,
      retryDelay: 2000, // 2 seconds
      maxRetryDelay: 30000, // 30 seconds
      concurrency: 2, // Number of concurrent uploads

      async startUploadProcess() {
        if (this.isRunning) {
          cLog('⚠️ Upload process already running');
          return;
        }

         // Check for stuck processing items and reset them to waiting
        const stuckItems = Cnvs.State.data.uploadQueue.filter(q => 
            q.status === Cnvs.State.UPLOAD_QUEUE_STATUS.PROCESSING
        );

        if (stuckItems.length > 0) {
            cLog(`⚠️ Found ${stuckItems.length} stuck items, resetting to WAITING`);
            stuckItems.forEach(item => {
                Cnvs.State.updateUploadQueueItem(item.frameId, {
                    status: Cnvs.State.UPLOAD_QUEUE_STATUS.WAITING
                });
            });
        }

        this.isRunning = true;
        cLog('🚀 Starting upload process');

        try {
          await this.processUploadQueue();
        } catch (error) {
          console.error('❌ Upload process error:', error);
        } finally {
          this.isRunning = false;
          cLog('⏹️ Upload process stopped');
        }
      },

      async processUploadQueue() {
        while (this.isRunning) {
          const queueStatus = Cnvs.State.getUploadQueueStatus();
          
          if (!queueStatus || queueStatus.waiting === 0) {
            // No items waiting, check if we should continue
            if (queueStatus && queueStatus.processing > 0) {
              // Still processing, wait a bit
              await this.delay(1000);
              continue;
            } else {
              // Nothing to process, exit
              cLog('✅ Upload queue empty, stopping process');
              break;
            }
          }

          // Process next batch
          const waitingItems = Cnvs.State.data.uploadQueue.filter(q => 
            q.status === Cnvs.State.UPLOAD_QUEUE_STATUS.WAITING
          ).slice(0, this.concurrency);

          if (waitingItems.length === 0) {
            await this.delay(1000);
            continue;
          }

          // Process items concurrently
          const uploadPromises = waitingItems.map(item => this.processUploadItem(item));
          await Promise.allSettled(uploadPromises);

          // Brief pause between batches
          await this.delay(500);
        }
      },

      async processUploadItem(queueItem) {
        const { frameId } = queueItem;
        const frame = Cnvs.State.getFrame(frameId);
        
        if (!frame) {
          cLog(`⚠️ Frame ${frameId} not found, removing from queue`);
          Cnvs.State.removeFromUploadQueue(frameId);
          return;
        }

        // Check if already uploaded
        if (frame.image?.status === Cnvs.State.IMAGE_STATUS.UPLOADED && frame.image?.filestackUrl) {
          cLog(`✅ Frame ${frameId} already uploaded, removing from queue`);
          Cnvs.State.removeFromUploadQueue(frameId);
          return;
        }

        // Check retry limit
        if (queueItem.attempts >= queueItem.maxAttempts) {
          cLog(`❌ Frame ${frameId} exceeded max attempts, marking as failed`);
          Cnvs.State.updateFrameUploadStatus(frameId, Cnvs.State.IMAGE_STATUS.ERROR, null, 'Max upload attempts exceeded');
          return;
        }

        try {
          cLog(`📤 Uploading frame ${frameId} (attempt ${queueItem.attempts + 1})`);
          
          // Mark as processing
          Cnvs.State.updateUploadQueueItem(frameId, {
            status: Cnvs.State.UPLOAD_QUEUE_STATUS.PROCESSING,
            attempts: queueItem.attempts + 1,
            lastAttemptAt: Date.now()
          });

          // Update frame status
          Cnvs.State.updateFrameUploadStatus(frameId, Cnvs.State.IMAGE_STATUS.UPLOADING);

          // Perform the upload
          let filestackUrl = null;
          if(frame.image.previewURL.indexOf('filestack') > -1){
            filestackUrl = frame.image.previewURL;
          }
          else{
            filestackUrl = await this.uploadFrameToFilestack(frame);
          }
          
          if (filestackUrl) {
            // Success
            cLog(`✅ Frame ${frameId} uploaded successfully: ${filestackUrl}`);
            delete frame?.image?.file;
            Cnvs.State.updateFrameUploadStatus(frameId, Cnvs.State.IMAGE_STATUS.UPLOADED, filestackUrl);
          } else {
            throw new Error('Upload returned null URL');
          }

        } catch (error) {
          console.error(`❌ Upload failed for frame ${frameId}:`, error);
          
          // Calculate retry delay with exponential backoff
          const delay = Math.min(this.retryDelay * Math.pow(2, queueItem.attempts), this.maxRetryDelay);
          
          // Mark as waiting for retry
          Cnvs.State.updateUploadQueueItem(frameId, {
            status: Cnvs.State.UPLOAD_QUEUE_STATUS.WAITING,
            error: error.message,
            nextRetryAt: Date.now() + delay
          });

          Cnvs.State.updateFrameUploadStatus(frameId, Cnvs.State.IMAGE_STATUS.PENDING, null, error.message);

          // Wait before retry
          await this.delay(delay);
        }
      },

      async uploadFrameToFilestack(frame) {
        if (!frame.image?.file && !frame.image?.previewURL) {
          throw new Error('No file or preview URL available for upload');
        }

        const client = w.filestack?.init('Av52UkFmsQ1aDQgxjEjyrz');
        if (!client) {
          throw new Error('Filestack not available');
        }

        let blob;
        
        if (frame.image.file) {
          // Use original file if available
          blob = frame.image.file;
        } else if (frame.image.previewURL) {
          // Convert preview URL to blob
          const response = await fetch(frame.image.previewURL);
          if (!response.ok) {
            throw new Error(`Failed to fetch preview image: ${response.statusText}`);
          }
          blob = await response.blob();
        } else {
          throw new Error('No usable image source found');
        }
        blob = await Cnvs.Images.resizeImageToMaxSize(blob);

        // Generate filename
        const timestamp = Date.now().toString(36);
        const randomId = Cnvs?.Utils?.makeId?.(8) || Math.random().toString(36).substr(2, 8);
        const filename = `f_f-${new Date().getMonth() + 1}_${new Date().getFullYear()}-${timestamp}${randomId}`;
        
        // Create file with proper name
        const fileToUpload = new File([blob], filename, { type: blob.type });
        
        // Upload to Filestack
        const uploadResult = await client.upload(fileToUpload);
        
        if (!uploadResult || !uploadResult.url) {
          throw new Error('Upload result invalid');
        }

        return uploadResult.url;
      },

      delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
      },

      stop() {
        this.isRunning = false;
        cLog('🛑 Upload process stop requested');
      }
    },

    // Start uploads for frames that need uploading
    async startUploadsForFrames(frameIds = null) {
      if (!frameIds) {
        // Get all frames that need uploading
        const framesNeedingUpload = Cnvs.State.getFramesNeedingUpload();
        frameIds = framesNeedingUpload.map(f => f.id);
      }

      if (frameIds.length === 0) {
        cLog('ℹ️ No frames need uploading');
        return;
      }

      cLog(`📋 Adding ${frameIds.length} frames to upload queue`);
      
      // Add frames to upload queue
      frameIds.forEach(frameId => {
        Cnvs.State.addToUploadQueue(frameId);
      });

      // Start upload process
      this.uploadManager.startUploadProcess();
    },

    // Resume uploads on page load
    async resumeUploads() {
      cLog('🔄 Resuming uploads from previous session');
      
      // Check for frames that need uploading
      const framesNeedingUpload = Cnvs.State.getFramesNeedingUpload();
      
      if (framesNeedingUpload.length > 0) {
        cLog(`📋 Found ${framesNeedingUpload.length} frames needing upload`);
        await this.startUploadsForFrames();
      } else {
        cLog('✅ No uploads needed to resume');
      }
    },

    // ================================================================
    // IMAGE TRANSFORMATIONS
    // ================================================================

    zoomIn() { 
      const zmSlider = S$G('SCRzoomSlider');
      var slidev = Math.abs(zmSlider.value) + ((Math.abs(zmSlider.max) - Math.abs(zmSlider.min)) / 50);
      zmSlider.value = slidev;
      zmSlider.dispatchEvent(new Event('input'));
    },
    zoomOut() {
      const zmSlider = S$G('SCRzoomSlider');
      var slidev = Math.abs(zmSlider.value) - ((Math.abs(zmSlider.max) - Math.abs(zmSlider.min)) / 50);
      zmSlider.value = slidev;
      zmSlider.dispatchEvent(new Event('input'));
    },

    zoomSliderChange(value) {
        return Cnvs?.Errors?.safe(() => {
        
        const zmSlider = S$G('SCRzoomSlider');
        if (!zmSlider) return;
        
        const min = parseFloat(zmSlider.min);
        const max = parseFloat(zmSlider.max);
        const val = Math.max(min, Math.min(parseFloat(value), max));
        const percent = ((val - min) / (max - min)) * 100;
        
        zmSlider.style.background = `linear-gradient(to right, #4A2014 0%, #4A2014 ${percent}%, #ddd ${percent}%, #ddd 100%)`;
        
        // (heavy logic)
        this.throttledZoomProcess(val, percent);
      }, 'Cnvs.Images.zoomSliderChange');
    },

    // debouncedZoomProcess = this.debounce(this.zoomProcess.bind(this), 50);
    initThrottle() {
      this.throttledZoomProcess = this.throttleWithRaf(this.zoomProcess.bind(this));
    },

    zoomProcess(value, percent){
        const EFC = document.querySelector('.dp_epu');
        const EFrame = document.querySelector('.dp_epu-body .frame');
        // Use popup state if available, fallback to DOM queries
        const pu = Cnvs.Popup.current;
        const ceB = pu.ceB; // ceB is the same as EFbox
        var sizeElement = pu.ceF.dataset.frameEdge == "Wrapped" ? pu.ceF.querySelector(".img-print") : pu.ceF.querySelector(".img-box");
        const ceI = pu.ceI; // ceI is the same as EFimg
        const cuI = pu.cuI; // ceI is the same as EFimg
        
        if (!ceI || !ceB) {
          console.warn('Required editing elements not found for zoom slider');
          return;
        }

        if (ceB) {
          const rect = ceB.getBoundingClientRect();
          const fRect = EFrame.getBoundingClientRect();
          const root = document.documentElement;

          root.style.setProperty('--b-left', `${rect.left}px`);
          root.style.setProperty('--b-top', `${rect.top}px`);
          root.style.setProperty('--p-hinches', `${pu.ceHeight}px`);
          root.style.setProperty('--p-winches', `${pu.ceWidth}px`);
          root.style.setProperty('--b-width', `${rect.width}px`);
          root.style.setProperty('--b-height', `${rect.height}px`);
          root.style.setProperty('--f-width', `${fRect.width}px`);
          root.style.setProperty('--f-height', `${fRect.height}px`);
          const zmrect = pu.zmSlider.getBoundingClientRect();
          root.style.setProperty('--zm-left', `${zmrect.left}px`);
          root.style.setProperty('--zm-top', `${zmrect.top}px`);
          root.style.setProperty('--zm-width', `${zmrect.width}px`);
          root.style.setProperty('--zm-height', `${zmrect.height}px`);
          Cnvs.Frames.updateThumbMask(pu.zmSlider);
        }

        EFrame.classList.add("no-transition");

        // Apply zoom logic (keeping existing complex logic)
        let ceSc = value / 10000;
        
        const posrotation = pu.posrotation || "h";
        
        if (posrotation == "v") {
          var minScaleX = sizeElement.clientWidth / pu.ceNH;
          var minScaleY = sizeElement.clientHeight / pu.ceNW;
        } else {
          var minScaleX = sizeElement.clientWidth / pu.ceNW;
          var minScaleY = sizeElement.clientHeight / pu.ceNH;
        }
        const minScale = Math.max(minScaleX, minScaleY);
        
        const scaleType = percent < 1 ? "min" : "more";
        cuI.dataset.scale = scaleType;
        ceI.dataset.scale = scaleType;

        // ceSc = Math.max(ceSc, minScale);
        // ceI.style.transform = `rotate(${pu.rotation || 0}deg) scale(${ceSc})`;
        const scale = Cnvs.Utils.getScale(ceI);
        const oldScale = scale || 1;

        ceSc = Math.max(ceSc, minScale);
        let newScale = parseFloat(ceSc);

        const boxRect = ceB.getBoundingClientRect();
        const boxCX = boxRect.left + boxRect.width / 2;
        const boxCY = boxRect.top + boxRect.height / 2;

        const imgRect = ceI.getBoundingClientRect();
        const imgCX = imgRect.left + imgRect.width / 2;
        const imgCY = imgRect.top + imgRect.height / 2;

        // Distance from image center to visible center
        const dx = boxCX - imgCX;
        const dy = boxCY - imgCY;

        ceI.style.transform = `rotate(${pu.rotation || 0}deg) scale(${newScale})`;
        
        const scaleFactor = newScale / oldScale;
        const newLeft = parseFloat(ceI.style.left || 0) - dx * (scaleFactor - 1);
        const newTop = parseFloat(ceI.style.top || 0) - dy * (scaleFactor - 1);

        ceI.style.left = `${newLeft}px`;
        ceI.style.top = `${newTop}px`;

        // Call adjustPosition if available
        Cnvs.Images.adjustPosition(ceI, sizeElement);
        
        if (EFrame._removeEditingImgTimeout) {
          clearTimeout(EFrame._removeEditingImgTimeout);
        }

        EFrame._removeEditingImgTimeout = setTimeout(() => {
          EFC.classList.remove('editing-img-container');
          EFrame.classList.remove('editing-img');
          EFrame.classList.remove("no-transition");
          EFrame._removeEditingImgTimeout = null;
        }, 500);

        // Mark that scale or position has changed
        if (pu.isOpen) {
          Cnvs.Popup.update({ 
            SoPchanged: true,
            ceScale: ceSc
          });
        }
    },

    onStart(event) {
      return Cnvs?.Errors?.safe(() => {
        event.preventDefault();
        // Initialize handlers if not already done
        if (!this.handlersInitialized) {
          this.initHandlers();
        }
        // Get required elements
        const EFimg = document.querySelector('.dp_epu-body img');
        const EFbox = document.querySelector('.dp_epu-body .img-box');
        const EFC = document.querySelector('.dp_epu');
        const EFrame = document.querySelector('.dp_epu-body .frame');
        const zmSlider = document.getElementById('SCRzoomSlider');
        
        if (!EFimg || !EFbox || !EFrame) {
          console.warn('Required editing elements not found for drag start');
          return;
        }

        // Only clear the timeout if zoomSliderChange is running, otherwise don't remove editing-img class here
        if (EFrame._removeEditingImgTimeout) {
          clearTimeout(EFrame._removeEditingImgTimeout);
          EFrame._removeEditingImgTimeout = null;
        } 
        EFC.classList.add('editing-img-container');
        EFrame.classList.add('editing-img');
        EFrame.classList.add("no-transition");

        // Use popup state if available, fallback to DOM queries
        const pu = Cnvs.Popup.current;

        if (EFbox) {
          const rect = EFbox.getBoundingClientRect();
          const fRect = EFrame.getBoundingClientRect();
          const root = document.documentElement;

          root.style.setProperty('--b-left', `${rect.left}px`);
          root.style.setProperty('--b-top', `${rect.top}px`);
          root.style.setProperty('--p-hinches', `${pu.ceHeight}px`);
          root.style.setProperty('--p-winches', `${pu.ceWidth}px`);
          root.style.setProperty('--b-width', `${rect.width}px`);
          root.style.setProperty('--b-height', `${rect.height}px`);
          root.style.setProperty('--f-width', `${fRect.width}px`);
          root.style.setProperty('--f-height', `${fRect.height}px`);
          const zmrect = pu.zmSlider.getBoundingClientRect();
          root.style.setProperty('--zm-left', `${zmrect.left}px`);
          root.style.setProperty('--zm-top', `${zmrect.top}px`);
          root.style.setProperty('--zm-width', `${zmrect.width}px`);
          root.style.setProperty('--zm-height', `${zmrect.height}px`);
          Cnvs.Frames.updateThumbMask(pu.zmSlider);
        }

        const clientX = event.clientX || event.touches[0].clientX;
        const clientY = event.clientY || event.touches[0].clientY;
        
        // Store drag state in the object
        this.dragState = {
          startX: clientX,
          startY: clientY,
          initialX: EFimg.offsetLeft,
          initialY: EFimg.offsetTop,
          isDragging: true
        };
        
        // Store global references for compatibility
        if (typeof w.startX !== 'undefined') w.startX = clientX;
        if (typeof w.startY !== 'undefined') w.startY = clientY;
        if (typeof w.initialX !== 'undefined') w.initialX = EFimg.offsetLeft;
        if (typeof w.initialY !== 'undefined') w.initialY = EFimg.offsetTop;
        if (typeof w.isD !== 'undefined') w.isD = true;
        if (typeof w.EFimg !== 'undefined') w.EFimg = EFimg;
        if (typeof w.EFbox !== 'undefined') w.EFbox = EFbox;
        if (typeof w.zmSlider !== 'undefined') w.zmSlider = zmSlider;
        
        EFimg.style.cursor = 'grabbing';

        document.addEventListener('mousemove', this.boundHandlers.move);
        document.addEventListener('mouseup', this.boundHandlers.end);
        document.addEventListener('mouseleave', this.boundHandlers.end);
        document.addEventListener('touchstart', this.boundHandlers.touchStart);
        document.addEventListener('touchmove', this.boundHandlers.touchMove);
        document.addEventListener('touchend', this.boundHandlers.touchEnd);
        document.addEventListener('touchcancel', this.boundHandlers.touchEnd);
        
      }, 'Cnvs.Images.onStart');
    },

    onEnd() {
      return Cnvs?.Errors?.safe(() => {
        // Reset drag state
        this.dragState = { isDragging: false };
        
        // Reset global variables
        if (typeof w.isD !== 'undefined') w.isD = false;
        if (typeof w.initialDistance !== 'undefined') w.initialDistance = null;
        
        const EFimg = document.querySelector('.dp_epu-body img');
        const EFC = document.querySelector('.dp_epu');
        const EFrame = document.querySelector('.dp_epu-body .frame');
        if(EFC) EFC.classList.remove('editing-img-container');
        if (EFrame) {
            EFrame.classList.remove('editing-img');
            EFrame.classList.remove('no-transition');
        }
        if (EFimg) EFimg.style.cursor = 'grab';

        document.removeEventListener('mousemove', this.boundHandlers.move);
        document.removeEventListener('mouseup', this.boundHandlers.end);
        document.removeEventListener('mouseleave', this.boundHandlers.end);
        document.removeEventListener('touchstart', this.boundHandlers.touchStart);
        document.removeEventListener('touchmove', this.boundHandlers.touchMove);
        document.removeEventListener('touchend', this.boundHandlers.touchEnd);
        document.removeEventListener('touchcancel', this.boundHandlers.touchEnd);
        if (EFrame) EFrame.classList.remove("no-transition");

      }, 'Cnvs.Images.onEnd');
    },

    onMove(event) {
      return Cnvs?.Errors?.safe(() => {
        event.preventDefault();
        
        // Use popup state if available, fallback to DOM queries
        const pu = Cnvs.Popup.current;
        const ceI = pu.isOpen ? pu.ceI : document.querySelector('.dp_epu-body img');
        const ceB = pu.isOpen ? pu.ceB : document.querySelector('.dp_epu-body .img-box');
        var sizeElement = pu.ceF.dataset.frameEdge == "Wrapped" ? pu.ceF.querySelector(".img-print") : pu.ceF.querySelector(".img-box");
        const zmSlider = document.getElementById('SCRzoomSlider');
        
        if (!ceI || !ceB) return;
        
        if (event.touches && event.touches.length === 2) {
          // Handle pinch zoom
          const newDistance = this.getTouchDistance(event.touches);
          
          if (w.initialDistance) {
            const scaleChange = newDistance / w.initialDistance;
            if (typeof w.pinchScale !== 'undefined') w.pinchScale *= scaleChange;
            w.initialDistance = newDistance;
            
            if (zmSlider) {
              const sliderMin = parseFloat(zmSlider.min);
              const sliderMax = parseFloat(zmSlider.max);
              let newSliderValue = (w.pinchScale - 1) * (sliderMax - sliderMin) + sliderMin;
              newSliderValue = Math.max(sliderMin, Math.min(sliderMax, newSliderValue));
              
              zmSlider.value = newSliderValue;
              zmSlider.dispatchEvent(new Event('input'));
              
              // Mark that scale or position has changed
              if (pu.isOpen) {
                Cnvs.Popup.update({ SoPchanged: true });
              }
            }
          }
        } else if (this.dragState?.isDragging) {
          // Handle drag movement
          requestAnimationFrame(() => {
            
            const clientX = event.clientX || event.touches[0].clientX;
            const clientY = event.clientY || event.touches[0].clientY;
            const dx = clientX - this.dragState.startX;
            const dy = clientY - this.dragState.startY;
            let newLeft = this.dragState.initialX + dx;
            let newTop = this.dragState.initialY + dy;

            ceI.style.left = `${newLeft}px`;
            ceI.style.top = `${newTop}px`;

            // Mark that scale or position has changed
            if (pu.isOpen) {
              Cnvs.Popup.update({ 
                SoPchanged: true,
                ceLeft: newLeft,
                ceTop: newTop
              });
            }
            w.SoPch = true; // Keep for backwards compatibility

            const imgRect = ceI.getBoundingClientRect();
            const frameRect = sizeElement.getBoundingClientRect();

            let offsetX = 0;
            let offsetY = 0;

            const margin = 0; 

            if (imgRect.left > frameRect.left + margin) {
              offsetX = frameRect.left - imgRect.left - margin;
            } else if (imgRect.right < frameRect.right - margin) {
              offsetX = frameRect.right - imgRect.right + margin;
            }

            if (imgRect.top > frameRect.top + margin) {
              offsetY = frameRect.top - imgRect.top - margin;
            } else if (imgRect.bottom < frameRect.bottom - margin) {
              offsetY = frameRect.bottom - imgRect.bottom + margin;
            }

            if (offsetX !== 0 || offsetY !== 0) {
              newLeft += offsetX;
              newTop += offsetY;
              ceI.style.left = `${newLeft}px`;
              ceI.style.top = `${newTop}px`;
              
              // Update popup state with corrected position
              if (pu.isOpen) {
                Cnvs.Popup.update({ 
                  ceLeft: newLeft,
                  ceTop: newTop
                });
              }
            }

            // Update global variables for compatibility
            if (typeof w.newL !== 'undefined') w.newL = newLeft;
            if (typeof w.newT !== 'undefined') w.newT = newTop;
          });
        }
        
      }, 'Cnvs.Images.onMove');
    },

    handlePinchStart(event) {
      return Cnvs?.Errors?.safe(() => {
        if (event.touches.length === 2) {
          w.initialDistance = this.getTouchDistance(event.touches);
          if (typeof w.pinchScale === 'undefined') w.pinchScale = 1;
        }
      }, 'Cnvs.Images.handlePinchStart');
    },

    getTouchDistance(touches) {
      return Cnvs?.Errors?.safe(() => {
        if (touches.length < 2) return 0;
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
      }, 'Cnvs.Images.getTouchDistance', 0);
    },

    // ================================================================
    // CROP OPERATIONS
    // ================================================================
    
    saveCrop(applyToAll = false) {
      return Cnvs?.Errors?.safe(() => {
        S$$('.framefh').forEach((frame) => {
          const ceB = frame.dataset.frameEdge == "Wrapped" ? frame.querySelector('.img-print') : frame.querySelector('.img-box');
          const ceP = frame.querySelector('.img-print');
          const ceI = frame.querySelector('.img-box img');
          const borderRect = ceB.getBoundingClientRect();
          const imgRect = ceI.getBoundingClientRect();
          const ceNW = ceI.naturalWidth;
          const fpd = parseFloat(getComputedStyle(ceP).left);
      
          var scaleDI = ceNW / imgRect.width,
      
          nx = Math.max(0,(borderRect.left - imgRect.left) * scaleDI),
          ny = Math.max(0,(borderRect.top - imgRect.top) * scaleDI),
          nwidth = borderRect.width * scaleDI,
          nheight = borderRect.height * scaleDI;

          frame.dataset.borderPx = frame.dataset.frameEdge == "Wrapped" ? 0 : Math.abs(Math.round(fpd * scaleDI));
          frame.dataset.crop = [Math.round(nx), Math.round(ny), Math.round(nwidth), Math.round(nheight)].join(',');
        });
      }, 'Cnvs.Images.saveCrop', null);
    },

    // ================================================================
    // IMAGE SAVING
    // ================================================================
    
    saveImage() {
      return Cnvs?.Errors?.safe(() => {
        const editPopup = document.querySelector('.dp_epu');
        if (editPopup) editPopup.style.display = 'none';
        
        Cnvs.Frames?.updateFrameSizes?.('edit');
        Cnvs.State?.scheduleSave?.();
        
        return true;
      }, 'Cnvs.Images.saveImage', false);
    },

    // ================================================================
    // UPLOAD OPERATIONS
    // ================================================================

    async uploadSingleFrame(frame) {
      return Cnvs?.Errors?.safeAsync(async () => {
        if (frame.dataset.image?.trim()) return frame.dataset.image;
        
        const client = w.filestack?.init('Av52UkFmsQ1aDQgxjEjyrz');
        if (!client) throw new Error('Filestack not available');
        
        const img = frame.querySelector('img');
        if (!img?.src) throw new Error('No image source found');
        
        const blob = await this.getBlobSmart(img);
        if (!blob) throw new Error('Failed to get image blob');
        blob = await Cnvs.Images.resizeImageToMaxSize(blob);

        const filename = `f_f-${new Date().getMonth() + 1}_${new Date().getFullYear()}-${Date.now().toString(36)}${Cnvs?.Utils?.makeId(8) || Math.random().toString(36).substr(2, 8)}`;
        const renamedBlob = new File([blob], filename, { type: blob.type });
        
        const uploadResult = await client.upload(renamedBlob);
        frame.dataset.image = uploadResult.url;
        
        return uploadResult.url;
      }, 'Cnvs.Images.uploadSingleFrame', null);
    },

    async uploadAllImagesToFilestack() {
      return Cnvs?.Errors?.safeAsync(async () => {
        if (!w.filestack) throw new Error('Filestack not available');
        
        const client = w.filestack.init('Av52UkFmsQ1aDQgxjEjyrz');
        const framesToUpload = Array.from(document.querySelectorAll('.framefh[data-image=""]'));
        
        const uploadPromises = framesToUpload.map(frame => this.uploadSingleFrame(frame));
        const results = await Promise.allSettled(uploadPromises);
        
        const successful = results.filter(r => r.status === 'fulfilled').map(r => r.value);
        const failed = results.filter(r => r.status === 'rejected');
        
        if (failed.length > 0) {
          console.warn('Some uploads failed:', failed.map(f => f.reason?.message));
        }
        
        return successful;
      }, 'Cnvs.Images.uploadAllImagesToFilestack', []);
    },

    // ================================================================
    // BLOB OPERATIONS
    // ================================================================
    
    getBlobFromImageElement(imgElement) {
      return Cnvs?.Errors?.safe(() => {
        if (!imgElement) return null;
        
        return new Promise((resolve, reject) => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          canvas.width = imgElement.naturalWidth || imgElement.width;
          canvas.height = imgElement.naturalHeight || imgElement.height;
          
          ctx.drawImage(imgElement, 0, 0);
          
          canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Failed to create blob')), 'image/png');
        });
      }, 'Cnvs.Images.getBlobFromImageElement', null);
    },

    async getBlobSmart(imgElement) {
      return Cnvs?.Errors?.safeAsync(async () => {
        if (!imgElement?.src) throw new Error('Invalid image element');
        
        if (imgElement.src.startsWith('blob:')) {
          try {
            const response = await fetch(imgElement.src);
            return await response.blob();
          } catch (e) {
            console.warn('Fetch failed, using canvas fallback');
          }
        }
        
        return await this.getBlobFromImageElement(imgElement);
      }, 'Cnvs.Images.getBlobSmart', null);
    },

    base64ToBlob(base64, mimeType = 'image/png') {
      return Cnvs?.Errors?.safe(() => {
        if (Cnvs.Utils?.base64ToBlob) return Cnvs.Utils.base64ToBlob(base64, mimeType);
        
        const byteCharacters = atob(base64.split(',')[1] || base64);
        const byteNumbers = Array.from(byteCharacters, char => char.charCodeAt(0));
        const byteArray = new Uint8Array(byteNumbers);
        
        return new Blob([byteArray], { type: mimeType });
      }, 'Cnvs.Images.base64ToBlob', null);
    },

    // ================================================================
    // FILESTACK SETUP
    // ================================================================
    
    setupFilestackUploadPanels() {
      return Cnvs?.Errors?.safe(() => {
        document.querySelectorAll('.dp_upu-upanel').forEach(panel => {
          panel.addEventListener('click', (e) => {
            const client = filestack.init('Av52UkFmsQ1aDQgxjEjyrz');
            const source = e.currentTarget.classList.contains("open_fb") 
              ? ["facebook", "instagram", "googlephotos"]
              : e.currentTarget.classList.contains("open_ig")
              ? ["instagram", "facebook", "googlephotos"]
              : ["googlephotos", "facebook", "instagram"];
            
            const options = {
              maxFiles: 20,
              uploadInBackground: false,
              fromSources: source,
              onOpen: () => console.log('opened!'),
              onUploadDone: (res) => this.previewImgs(res),
            };
            client.picker(options).open();
          });
        });
        
        return true;
      }, 'Cnvs.Images.setupFilestackUploadPanels', false);
    },

    // ================================================================
    // IMAGE PREVIEW & PROCESSING
    // ================================================================
    
    // concurrency limiter (prevents iPhone crashes)
    async processWithLimit(items, limit, worker) {
      const results = [];
      const executing = [];

      for (const item of items) {
        const p = Promise.resolve().then(() => worker(item));
        results.push(p);

        if (limit <= items.length) {
          const e = p.then(() =>
            executing.splice(executing.indexOf(e), 1)
          );
          executing.push(e);

          if (executing.length >= limit) {
            await Promise.race(executing);
          }
        }
      }

      return Promise.allSettled(results);
    },

    previewImgs(event) {
      return Cnvs?.Errors?.safe(async () => {
        const isFilestack = event.filesUploaded !== undefined;
        const files = isFilestack ? event.filesUploaded : Array.from(event.target.files || []);

        if (!files.length) return;

        // Clear any loading/error states but keep button enabled
        Cnvs?.State?.updateLoading?.('upload', true);
        Cnvs?.State?.clearError?.('upload');
        const nowISO = new Date().toISOString();

        const currentFrameCount = document.querySelectorAll(".frame .img-box img").length;
        const imgPreview = document.getElementById('img-preview');
        if (files.length > 0 && imgPreview) {
          imgPreview.classList.remove('img-thumbs-hidden');
        }

        w._originalFiles = isFilestack ? files.map(() => null) : files;

        // device safe concurrency
        const isMobile = /iPhone|iPad|Android/i.test(navigator.userAgent);
        const CONCURRENCY = isMobile ? 2 : 4;

        const results = await Cnvs.Images.processWithLimit(
          files.map((file, index) => ({ file, index })),
          CONCURRENCY,
          async ({ file, index }) => {
            try {
              // yield to keep UI responsive
              await new Promise(r => setTimeout(r, 0));

              file = await Cnvs.Images.resizeImageToMaxSize(file);

              const imgUrl = isFilestack ? file.url : (Cnvs?.Memory?.createBlob(file, `upload_${index}`) || URL.createObjectURL(file));
              const frameElement = Cnvs?.Frames?.addFrame?.(imgUrl, file);
              if (!frameElement) {
                throw new Error(`Frame create failed ${index}`);
              }

              // attach index for local files
              if (!isFilestack && file) {
                const imgElement = frameElement.querySelector('img');
                if (imgElement) imgElement.dataset.fileIndex = index;
              }

              const frameId = frameElement.dataset.frameId;
              if (frameId) {
                const frameUpdates = {
                  metadata: {
                    ...Cnvs?.State?.getState()?.frames?.find(f => f.id === frameId)?.metadata,
                    uploadSource: isFilestack ? 'filestack' : 'local',
                    uploadIndex: index,
                    originalFile: isFilestack ? null : file,
                    uploadTimestamp: new Date().toISOString()
                  }
                };

                if (isFilestack && file.url) {
                  frameUpdates.image = {
                    filestackUrl: file.url,
                    status: Cnvs.State.IMAGE_STATUS.UPLOADED,
                    previewURL: file.url
                  };
                }

                Cnvs?.State?.updateFrame?.(frameId, frameUpdates);
              }

              // robust image readiness
              let img = null;
              if (frameElement.tagName === "IMG") {
                img = frameElement;
              } else {
                img = frameElement.querySelector("img");
              }

              if (img) {
                try {
                  await Promise.race([
                    img.decode(),
                    new Promise((_, reject) =>
                      setTimeout(() => reject(new Error("decode timeout")), 15000)
                    )
                  ]);
                } catch {
                  // fallback if decode fails
                }

                // RESTORED: frame visibility + natural size
                const frame = img.closest('.frame');
                const frameContainer = img.closest('.frame_container');
                if (frame && frameContainer) {
                  frameContainer.classList.remove("frame_hidden");
                  frame.dataset.imageWidth = img.naturalWidth;
                  frame.dataset.imageHeight = img.naturalHeight;

                  const frameId = frame.dataset.frameId;
                  if (frameId) {
                    const currentFrame = Cnvs?.State?.getFrame?.(frameId);
                    if (currentFrame?.image) {
                      Cnvs?.State?.updateFrame?.(frameId, {
                        image: {
                          ...currentFrame.image,
                          naturalWidth: img.naturalWidth,
                          naturalHeight: img.naturalHeight,
                          loaded: true
                        }
                      });
                    }
                  }
                }
              }

              return { success: true };

            } catch (err) {
              console.error("Upload error:", err);
              return { success: false, error: err };
            }
          }
        );

        // accurate failure handling
        const failed = results.filter(r => r.value?.success === false);
        if (failed.length) {
          Cnvs?.State?.setError?.(
            'upload',
            `${failed.length} files failed to process`
          );
        }

        const successCount = results.filter(r => r.value?.success).length;

        Cnvs.Images.finalizeImageProcessing(currentFrameCount, successCount);
        if (!isFilestack && event.target) {
          event.target.value = '';
        }
      }, 'Cnvs.Images.previewImgs');
    },

    finalizeImageProcessing(originalCount, newCount) {
      return Cnvs?.Errors?.safe(() => {
        if (typeof w.lefttup !== 'undefined') w.lefttup = NaN;
        
        Cnvs?.Frames?.setFID?.();
        
        if (w.up2fs && typeof w.up2fs === 'function') w.up2fs();
        Cnvs?.Frames?.updateFrameSizes?.('first');
        Cnvs?.Frames?.checkPPI?.();
        
        Cnvs?.State?.updateComputed?.();
        Cnvs?.State?.updateLoading?.('upload', false);
        Cnvs?.State?.triggerUpdate?.('uploadComplete');
        
        const frames = document.querySelectorAll('.framefh');
        if (frames.length > 0) {
          // Update cart button state properly
          if (Cnvs.Cart && typeof Cnvs.Cart.updateAddToCartButton === 'function') {
            Cnvs.Cart.updateAddToCartButton();
          } else {
            // Fallback: just ensure button is enabled
            const atcBtn = document.querySelector('.dp_st__atc-btn');
            if (atcBtn) atcBtn.classList.remove('disa');
          }
        }

        // Start uploading new frames immediately
        if (newCount > 0) {
          cLog(`🚀 Starting uploads for ${newCount} new frames`);
          setTimeout(() => {
            this.startUploadsForFrames();
          }, 1000); // Small delay to ensure frames are fully processed
        }
        
        return { originalCount, newCount, totalFrames: frames.length };
      }, 'Cnvs.Images.finalizeImageProcessing', null);
    },

    // ================================================================
    // EDITING SETUP
    // ================================================================
    boundHandlers: {},

    initHandlers() {
      if (this.handlersInitialized) return;

      this.boundHandlers = {
        move: this.onMove.bind(this),
        end: this.onEnd.bind(this),
        touchStart: this.handlePinchStart.bind(this),
        touchMove: this.onMove.bind(this),
        touchEnd: this.onEnd.bind(this),
      };
       this.handlersInitialized = true;
    },
    
    loadImageForEditing() {
      return Cnvs?.Errors?.safe(async () => {
        // Get popup state - if not available, popup isn't open
        const pu = Cnvs.Popup.current;
        if (!pu.isOpen) {
          console.warn('⚠️ Popup not open, cannot load image for editing');
          return null;
        }
        
        // Reset variables
        if (typeof w.isD !== 'undefined') w.isD = false;
        
        const ceF = pu.ceF;
        const ceB = pu.ceB;
        let frameEdge = pu.ceF.dataset.frameEdge;
        var sizeElement = frameEdge == "Wrapped" ? pu.ceF.querySelector(".img-print") : pu.ceF.querySelector(".img-box");
        var cuSizeElement = frameEdge == "Wrapped" ? pu.cuF.querySelector(".img-print") : pu.cuF.querySelector(".img-box");
        const cuF = pu.cuF;
        const ceI = pu.ceI;
        const ceScale = getComputedStyle(ceI).transform.split('(')[1].split(')')[0].split(',')[0];
        const zmSlider = pu.zmSlider;
        
        if (!ceF || !ceB || !ceI) throw new Error('Required editing elements not found in popup state');
        
        ceF.addEventListener('mousedown', (e) => Cnvs.Images.onStart(e));
        ceF.addEventListener('touchstart', (e) => Cnvs.Images.onStart(e));
        
        const nextFrame = () => new Promise(res => requestAnimationFrame(res));
        await nextFrame();
        await nextFrame();

        let minScaleX, minScaleY, boxw, boxh, PopupRatio;

        if(ceF.dataset.widthInInches == cuF.dataset.widthInInches  && ceF.dataset.heightInInches == cuF.dataset.heightInInches && ceF.dataset.frameEdge == cuF.dataset.frameEdge) {
          PopupRatio = parseInt(ceF.style.height) / parseInt(cuF.style.height);
          boxw = cuSizeElement.clientWidth;
          boxh = cuSizeElement.clientHeight;
        } else {
          PopupRatio = 1;
          boxw = sizeElement.clientWidth;
          boxh = sizeElement.clientHeight;
        }

        minScaleX = boxw / pu.ceNW * PopupRatio;
        minScaleY = boxh / pu.ceNH * PopupRatio;
        
        const ceMiS = Math.max(minScaleX, minScaleY);
        const [frameSizeW, frameSizeH] = pu.ceSize.split('x').map(Number);
        const printingAreaW = frameEdge == "Wrapped" ? frameSizeW + 3 : frameSizeW;
        const printingAreaH = frameEdge == "Wrapped" ? frameSizeH + 3 : frameSizeH;
        const paperDiagonalInches = Math.sqrt(printingAreaW ** 2 + printingAreaH ** 2);
        const imageDiagonal = Math.sqrt(((parseFloat(w.getComputedStyle(sizeElement).width)) * (1 / ceMiS)) ** 2 + ((parseFloat(w.getComputedStyle(sizeElement).height)) * (1 / ceMiS)) ** 2);
        const currentPPI = imageDiagonal / paperDiagonalInches;
        
        if (currentPPI <= 100) {
          console.log("currentPPI <= 100", currentPPI);
          const maxZoomOutScale = ceMiS * 1.33;
          Cnvs.Images.setSliderRange(ceMiS * 10000, maxZoomOutScale * 10000, ceScale * 10000);
        } else {
          console.log("currentPPI > 100", currentPPI);
          const maxZoomOutScale = ceMiS * ((currentPPI * 0.98) / 100);
          const maxZoomOutScaleBU = ceMiS * 1.33;
          const ceMaS = Math.max(ceMiS, maxZoomOutScale, maxZoomOutScaleBU);
          Cnvs.Images.setSliderRange(ceMiS * 10000, ceMaS * 10000, ceScale * 10000);
        }

        // Setup wheel events
        const wheelHandler = (event) => {
          event.preventDefault();
          if (event.deltaY < 0) {
            this.zoomIn(event);
          } else {
            this.zoomOut(event);
          }
        };
        
        ceF.addEventListener('wheel', wheelHandler);
        if (zmSlider) zmSlider.addEventListener('wheel', wheelHandler);
        
        return {
          frame: ceF,
          box: ceB,
          img: ceI,
          slider: zmSlider,
          currentPPI: currentPPI,
          minScale: ceMiS,
          currentScale: pu.ceScale
        };
      }, 'Cnvs.Images.loadImageForEditing', null);
    },

    setupZoomButtons() {
      return Cnvs?.Errors?.safe(() => {
        const zoomButtons = S$$(".dp_epu-zoom-container button");
        const zmSlider = S$G('SCRzoomSlider');
        if (zmSlider) {
          zmSlider.addEventListener("input", () => {
            const EFC = document.querySelector('.dp_epu');
            const EFrame = document.querySelector('.dp_epu-body .frame');
            EFC.classList.add('editing-img-container');
            EFrame.classList.add('editing-img');
            this.zoomSliderChange(parseFloat(zmSlider.value));
          });
        }
        if (!zmSlider) {
          console.warn('Zoom slider not found for zoom buttons setup');
          return false;
        }
        
        zoomButtons.forEach(button => {
          const eventType = window.dType === "D" ? "click" : "touchstart";

          button.addEventListener(eventType, (e) => {
            e.preventDefault();
            
            const min = parseFloat(zmSlider.min);
            const max = parseFloat(zmSlider.max);
            const currentValue = parseFloat(zmSlider.value);
            const isZoomIn = button.classList.contains("dp_epu-zoom-plus");
            
            // const percentage = isZoomIn ? 0.05 : -0.05;
            // let newValue = currentValue + (currentValue * percentage);
            const step = (max - min) * 0.05; // 5% of range
            let newValue = isZoomIn ? currentValue + step : currentValue - step;
            
            if (newValue <= min + 0.1) {
              newValue = min;
            } else if (newValue >= max - 0.1) {
              newValue = max;
            }
            
            newValue = parseFloat(Math.max(min, Math.min(newValue, max)).toFixed(2));
            
            // Only proceed if value actually changed
            if (Math.abs(newValue - currentValue) < 0.01) return;
            
            zmSlider.value = newValue;
            
            // requestAnimationFrame(() => {
              this.zoomSliderChange(newValue);
            // });
          });
        
        });
        
        return true;
      }, 'Cnvs.Images.setupZoomButtons', false);
    },

    // ================================================================
    // UTILITY FUNCTIONS
    // ================================================================

    adjustPosition(ceI, sizeElement) {
      const imgRect = ceI.getBoundingClientRect();
      const frameRect = sizeElement.getBoundingClientRect();
    
      let parsedLeft = parseFloat(ceI.style.left);
      let parsedTop = parseFloat(ceI.style.top);

      let newLeft = Number.isNaN(parsedLeft) ? ceI.offsetLeft : parsedLeft;
      let newTop = Number.isNaN(parsedTop) ? ceI.offsetTop : parsedTop;

      if (Math.round(imgRect.left) > Math.round(frameRect.left)) {
        newLeft = Math.round(frameRect.left) - Math.round(imgRect.left) + ceI.offsetLeft;
      }
      if (Math.round(imgRect.top) > Math.round(frameRect.top)) {
        newTop = Math.round(frameRect.top) - Math.round(imgRect.top) + ceI.offsetTop;
      }
      if (Math.round(imgRect.right) < Math.round(frameRect.right)) {
        newLeft = Math.round(frameRect.right) - Math.round(imgRect.right) + ceI.offsetLeft;
      }
      if (Math.round(imgRect.bottom) < Math.round(frameRect.bottom)) {
        newTop = Math.round(frameRect.bottom) - Math.round(imgRect.bottom) + ceI.offsetTop;
      }
      ceI.style.left = `${newLeft}px`;
      ceI.style.top = `${newTop}px`;
    
      Cnvs.Popup.update({
        ceLeft: newLeft,
        ceTop: newTop
      });
    },

    setSliderRange(min, max, val) {
      const zmSlider = document.getElementById('SCRzoomSlider');
      val = Math.max(min, Math.min(val, max)); 
      zmSlider.min = min;
      zmSlider.max = max;
      zmSlider.value = val;
      const percent = ((val - min) / (max - min)) * 100;
      zmSlider.style.background = `linear-gradient(to right, #4A2014 0%, #4A2014 ${percent}%, #ddd ${percent}%, #ddd 100%)`;
    },
    
    // Resizes an uploaded image while preserving quality and aspect ratio.
    resizeImageToMaxSize(file, maxSize = 2999) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);

        img.onload = () => {
          let { width, height } = img;

          if (width * height > 20000000) {
            console.warn("⚠️ Extremely large image detected");
          }

          if (Math.max(width, height) <= maxSize) {
            URL.revokeObjectURL(url);
            return resolve(file);
          }

          const scale = Math.min(maxSize / width, maxSize / height);
          const newWidth = Math.round(width * scale);
          const newHeight = Math.round(height * scale);

          // console.log(`Original: ${width}×${height}`);
          // console.log(`Resized : ${newWidth}×${newHeight}`);

          const canvas = document.createElement("canvas");
          canvas.width = newWidth;
          canvas.height = newHeight;

          const ctx = canvas.getContext("2d");

          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = "high";

          ctx.drawImage(img, 0, 0, newWidth, newHeight);

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                URL.revokeObjectURL(url);
                return reject("Canvas export failed");
              }

              const resizedFile = new File([blob], file.name, {
                type: file.type,
              });

              const oldSizeMB = (file.size / (1024 * 1024)).toFixed(2);
              const newSizeMB = (resizedFile.size / (1024 * 1024)).toFixed(2);

              // console.log(`Original size: ${oldSizeMB} MB`);
              // console.log(`Compressed size: ${newSizeMB} MB`);

              URL.revokeObjectURL(url);
              resolve(resizedFile);
            },
            file.type,
            0.92 // slightly safer default
          );
        };

        img.onerror = (err) => {
          URL.revokeObjectURL(url);
          reject(err);
        };

        img.src = url;
      });
    },

    keepImageCenter(img, newScale) {
      return Cnvs?.Errors?.safe(() => {
        if (Cnvs.Utils?.keepImageCenter) return Cnvs.Utils.keepImageCenter(img, newScale);
        
        if (!img?.style) return false;
        
        const oldScale = Cnvs.Utils.getScale(img);
        const container = img.parentElement;
        
        if (container) {
          const centerX = container.offsetWidth / 2;
          const centerY = container.offsetHeight / 2;
          
          const newLeft = centerX - (img.naturalWidth * newScale) / 2;
          const newTop = centerY - (img.naturalHeight * newScale) / 2;
          
          img.style.left = `${newLeft}px`;
          img.style.top = `${newTop}px`;
          img.style.transform = `scale(${newScale})`;
        }
        
        return true;
      }, 'Cnvs.Images.keepImageCenter', false);
    },

    // throttled function using requestAnimationFrame.
    throttleWithRaf(fn) {
        let scheduled = false;
        let lastArgs, lastContext;

        return function (...args) {
            lastArgs = args;
            lastContext = this;

            if (!scheduled) {
            scheduled = true;
            requestAnimationFrame(() => {
                scheduled = false;
                fn.apply(lastContext, lastArgs);
            });
            }
        };
    },

    logInfo() {
      cLog('📦 Cnvs.Images module loaded');
      cLog('🖼️ Available functions:', [
        'zoom()', 'saveCrop()', 'saveImage()',
        'uploadImagesToFilestack()', 'previewImgs()', 'loadImageForEditing()'
      ]);
    }
  };

  // ================================================================
  // MODULE INITIALIZATION
  // ================================================================
  
  Cnvs.Images.setupZoomButtons();
  Cnvs.Images.setupFilestackUploadPanels();
  Cnvs.Images.initThrottle();

  if (Cnvs.moduleStatus) {
    Cnvs.moduleStatus.images = true;
    cLog('✅ Cnvs.Images module registered');
    if (Cnvs.checkModules) Cnvs.checkModules();
  }

  if (typeof w !== 'undefined' && !Cnvs._silentLoad) {
    Cnvs.Images.logInfo();
  }

})(window);